import React from 'react';
import { focusNonFocusableElementAndToggleClassName } from '../../../lib/DomUtils/utils';
import { BlockInteractionsState } from './BlockInteractionsTypes';
import { HeadingHierarchy } from '../../../_lib/components/Heading/Heading';

export enum Views {
  CREATE = 'create',
  DEFAULT = 'default',
  DELETE = 'delete',
  DELETE_BLOCK = 'deleteBlock',
  CONNECT_BLOCKS = 'connectBlocks',
  EDIT = 'edit',
  EXPORT_AS_CSV = 'exportAsCsv',
  // special instant action
  INVITATION = 'invitation',
  INVITE = 'invite',
  ADD = 'add',
  REQUEST_ACTIVATION = 'requestActivation',
  ACCEPT_ACTIVATION = 'acceptActivation',
  REJECT_ACTIVATION = 'rejectActivation',
  // special view for inviting unregistered users
  INVITE_UNREGISTERED = 'inviteUnregistered',
  JOIN = 'join',
  JOINREQUEST = 'joinRequest',
  LEAVE = 'leave',
  MANAGE = 'manage',
  ACTION = 'action',
}

/** ContentWrapper actions that are considered as views in BlockInteractions */
export const ActionViews = ['deleteBlock', 'connectBlocks', 'add'];

export enum DISPLAY_TYPE {
  COLLAPSIBLE = 1,
  MODAL = 3,
  MODAl_WIDE = 4,
  NONE = 0,
}

const PRIMARY_VIEWS: string[] = [Views.DEFAULT, Views.EDIT, Views.MANAGE];
const INVITE_VIEWS: string[] = [Views.INVITE, Views.INVITATION, Views.INVITE_UNREGISTERED, Views.ADD];

const INITIAL_STATE: BlockInteractionsState = {
  primaryView: Views.DEFAULT,
  secondaryView: undefined,
};

/** A function returning the next state given a view name and an optional state
 *
 * Depending on the kind of view it either sets the primaryView or
 * secondaryView.
 */
export const computeState = (viewName?: string, state = INITIAL_STATE): BlockInteractionsState => {
  const isPrimary = viewName && PRIMARY_VIEWS.includes(viewName);
  const key = isPrimary ? 'primaryView' : 'secondaryView';
  let value: string | undefined = viewName;

  let { primaryView } = state;
  const { secondaryView } = state;

  // toggling primaryView defaults to Views.DEFAULT while
  // toggling the secondaryView defaults to null
  if (state[key] === viewName) {
    value = isPrimary ? Views.DEFAULT : undefined;
  }

  // SonLocke: <Members/>: if the invite view is toggled the manage view
  if (viewName && INVITE_VIEWS.includes(viewName) && primaryView === Views.MANAGE) {
    primaryView = 'default';
  }

  return {
    primaryView,
    secondaryView,
    [key]: value,
  };
};

/**
 * Focus the headline of a block based on its ref.
 * Visually we outline the whole block to make it clear where we are after refetching the block data.
 * All extra stuff needed (tabindex, class) is removed on blur.
 */
export const focusHeadlineFromRefAndOutlineBlock = (element: React.RefObject<HTMLElement | null>) => {
  if (element.current) {
    const parentBlock = element.current.closest('.block-interactions');
    if (parentBlock) {
      focusNonFocusableElementAndToggleClassName(element.current, parentBlock, 'block-interactions--focus-on-headline');
    }
  }
};

/**
 * key - Key identifies the view connected to the button
 * svgSpriteId - Maps the used icon for the button
 * transNamespaceAndKey - Namespace and Key for the used translations
 * displayType - Determines how view is presented to the user for example as Collapsible or Modal
 * View {JSX.Element=} - View that will be opend when clicking the button
 * initialFocusIndex - Determines which button will initially receive focus
 * returnAnyway - needed for buttons, which do not have svgSpriteId, i.e.: "Freigabe beantragen"
 * isDisabled - Disables or Enables the button
 * Used to allow buttons that just trigger an action, callback, not opening any view.
 * (workaround for on2 content copy button [SON-10763])
 * */
export const createButtonObject = (
  key: string,
  svgSpriteId: string,
  transNamespaceAndKey: string,
  displayType: DISPLAY_TYPE,
  View: any = null,
  initialFocusIndex: number | null = null,
  returnAnyway = false,
  isDisabled = false,
  action: any = null,
) => {
  if ((key && svgSpriteId && transNamespaceAndKey && displayType !== null) || returnAnyway) {
    return {
      [key]: {
        svgSpriteId,
        transNamespaceAndKey,
        displayType,
        View,
        initialFocusIndex,
        isDisabled,
        action,
      },
    };
  }
  return null;
};

/**
 * To keep ID convention centralized we built this
 * to create a unique id string of elementType and uuid.
 * This is important for block identifying and focusing.
 * It's used in ContentWrapper too.
 * @param elementType
 * @param uuid
 * @return {string}
 */
export function idCreator(elementType: string, uuid: string): string {
  if (elementType && uuid) {
    return `${elementType}-${uuid}`;
  }
  throw Error('no elementType or uuid provided');
}

/**
 * returns the hierarchy of blocks in (nested) content wrappers
 */
export const headlineHierarchy = (
  baseHierarchy: HeadingHierarchy,
  parentIndex: number | undefined,
  grandParentIndex: number | undefined,
): HeadingHierarchy => {
  // nested twice
  if (
    parentIndex !== undefined &&
    grandParentIndex !== undefined
  ) {
    return baseHierarchy + 2 as HeadingHierarchy;
  }
  // nested once
  if (parentIndex !== undefined) {
    return baseHierarchy + 1 as HeadingHierarchy;
  }
  // not nested
  return baseHierarchy as HeadingHierarchy;
};
